import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { logger } from '../lib/logger';
import { useApi } from '../lib/api';
import { ApiProblemError, fetchJson } from '../lib/apiFetchJson';
import { ViewPreferencesResponseSchema } from '@shared/schemas/view-preferences.schema';
import type { FlowRole } from '@shared/flow/role';
import { FlowAccountLayoutIdSchema, FlowHomeLayoutIdSchema } from '@shared/flow/flow-layouts';
import type {
  FlowAccountLayoutId,
  FlowHomeLayoutId,
  FlowLayoutSlot,
  FlowLayoutTable,
  FlowListLayoutChoices,
  FlowListLayoutId,
} from '@shared/flow/flow-layouts';
import type { AccountTab } from '../components/account-view/types';

type LayoutMode = 'cards' | 'mail';

export interface LayoutPrefs {
  layoutMode: LayoutMode;
  pipelineLayoutMode: LayoutMode;
  defaultAccountTab: AccountTab;
  /**
   * "Show accounts beyond the ones I own", the same saved preference the
   * Preferences dialog writes. Read-only here: surfaces that scope by ownership
   * follow it rather than each offering their own local toggle, so a rep sets it
   * once instead of per page.
   */
  showAllAccounts: boolean;
  /** Rep opt-in to Terra Beta (terraDesignV2), the opt-in for gating future work; nothing is gated on it today. Server-saved, read via useTerraDesignV2Enabled. */
  terraDesignV2: boolean;
  /** Finding phone numbers for contacts that have none (Dial and enrich): ON by default, a rep can turn it off. Server-saved, read via useFindMissingNumbersPref. */
  findMissingNumbers: boolean;
  /**
   * Role view switcher lens: the team the rep chose to view as, or `null`
   * for "follow my real team". Server-normalized (savePrefs stores NULL
   * when the chosen team equals the real one) — read via useRoleView.
   */
  viewAsRole: FlowRole | null;
  /**
   * The rep's own Flow layout choice per surface, or `null` for "follow the UI
   * View default". Schema-validated by ViewPreferencesResponseSchema; read via
   * `useFlowLayouts`, which resolves them against the UI View.
   */
  flowLayoutAccount: FlowAccountLayoutId | null;
  flowLayoutHome: FlowHomeLayoutId | null;
  /** Per-table list layout choices; the whole map is written on every change. */
  flowLayoutList: FlowListLayoutChoices | null;
}

/**
 * Per client, the latest INTENDED per-table list-layout map while writes are in
 * flight, plus how many are. `setFlowLayoutChoice` updates it synchronously
 * (the mutation's optimistic cache write only lands after an awaited
 * `cancelQueries`), so back-to-back calls compose instead of each reading the
 * same stale cache. Cleared when the last in-flight write settles.
 */
const pendingFlowLayoutList = new WeakMap<QueryClient, { map: FlowListLayoutChoices; inFlight: number }>();

export const PREFS_KEY = ['app-home', 'prefs'] as const;

/**
 * Every write from every `useLayoutPrefs` instance shares this key and this
 * scope, so writes run one at a time in the order they were made.
 */
const PREFS_MUTATION_KEY = ['layout-prefs'] as const;

const ACCOUNT_TAB_VALUES = new Set(['activity', 'intelligence', 'chat']);

const DEFAULTS: LayoutPrefs = {
  layoutMode: 'cards',
  pipelineLayoutMode: 'cards',
  defaultAccountTab: 'activity',
  showAllAccounts: false,
  terraDesignV2: false,
  findMissingNumbers: true,
  viewAsRole: null,
  flowLayoutAccount: null,
  flowLayoutHome: null,
  flowLayoutList: null,
};

/**
 * Server-backed layout mode preferences with optimistic updates.
 * Replaces URL-based layoutMode / pipelineLayoutMode params.
 */
export function useLayoutPrefs() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  const query = useQuery<LayoutPrefs>({
    queryKey: PREFS_KEY,
    queryFn: async () => {
      let data;
      try {
        data = await fetchJson(apiFetch, ViewPreferencesResponseSchema, '/api/v4/users/me/view-preferences');
      } catch (err) {
        // Every failure throws. TanStack keeps the last loaded prefs on a failed
        // refetch and marks the query stale, so the next consumer mount or a
        // reconnect retries; a failed first GET leaves `data` undefined and
        // DEFAULTS stand in (`prefsAvailable` false) until a retry succeeds.
        // Never a silent DEFAULTS "success": that read a stored lens as "no lens".
        if (err instanceof ApiProblemError) {
          logger.warn({ kind: err.kind, status: err.status }, '[layout-prefs] view-preferences GET failed');
        }
        throw err;
      }
      return {
        layoutMode: data.layoutMode === 'mail' ? 'mail' : 'cards',
        pipelineLayoutMode: data.pipelineLayoutMode === 'mail' ? 'mail' : 'cards',
        defaultAccountTab: ACCOUNT_TAB_VALUES.has(data.defaultAccountTab ?? '') ? data.defaultAccountTab as AccountTab : 'activity',
        showAllAccounts: !!data.showAllAccounts,
        terraDesignV2: !!data.terraDesignV2,
        findMissingNumbers: !!data.findMissingNumbers,
        // Already schema-validated (FlowRoleSchema.nullable().optional()) by
        // ViewPreferencesResponseSchema — no local re-validation needed.
        viewAsRole: data.viewAsRole ?? null,
        flowLayoutAccount: data.flowLayoutAccount ?? null,
        flowLayoutHome: data.flowLayoutHome ?? null,
        flowLayoutList: data.flowLayoutList ?? null,
      };
    },
    staleTime: Infinity,
    // A failed GET degrades to DEFAULTS (and the lens to /api/me's answer in
    // useRoleView); there is no action for a rep to take, so no global toast.
    meta: { suppressErrorToast: true },
  });

  const mutation = useMutation({
    mutationKey: PREFS_MUTATION_KEY,
    scope: { id: 'layout-prefs' },
    mutationFn: async (payload: Partial<LayoutPrefs>) => {
      return fetchJson(apiFetch, ViewPreferencesResponseSchema, '/api/v4/users/me/view-preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: PREFS_KEY });
      const snapshot = queryClient.getQueryData<LayoutPrefs>(PREFS_KEY);
      queryClient.setQueryData<LayoutPrefs>(PREFS_KEY, (old) => ({
        ...(old ?? DEFAULTS),
        ...payload,
      }));
      return { snapshot };
    },
    onError: (err, _payload, context) => {
      // Malformed 2xx: the server committed the write and the response body is never used.
      if (err instanceof ApiProblemError && err.kind === 'malformed') return;
      // A newer write is still queued behind this one. Its optimistic value is
      // the rep's latest choice, so this older snapshot is not restored over it.
      // This write is itself still pending here, so a count above one means a newer write.
      if (queryClient.isMutating({ mutationKey: PREFS_MUTATION_KEY }) > 1) {
        logger.warn('Layout preference save failed, a newer save is queued');
        return;
      }
      if (context?.snapshot) queryClient.setQueryData(PREFS_KEY, context.snapshot);
      logger.warn('Layout preference save failed, rolled back');
    },
    onSettled: (_data, _err, variables) => {
      // A newer write is still queued behind this one. Its optimistic value is the rep's latest choice, and a refetch now would land this write's server row over it, so the last queued write's settle refetches instead. This write is itself still pending here, as in onError, so a count above one means a newer write.
      if (queryClient.isMutating({ mutationKey: PREFS_MUTATION_KEY }) <= 1) {
        void queryClient.invalidateQueries({ queryKey: PREFS_KEY });
      }
      // A lens write changes /api/me's EFFECTIVE userType/flowRole, which
      // role-gated UI reads, so /api/me is refetched. The lens itself is read
      // from this hook's prefs cache (useRoleView), so nothing patches /api/me
      // optimistically. The Flow identity is never refetched: the lens is UI-only.
      if ('viewAsRole' in variables) {
        void queryClient.invalidateQueries({ queryKey: ['/api/me'] });
      }
    },
  });

  const current = query.data ?? DEFAULTS;

  // Destructured to a plain local so `react-hooks/exhaustive-deps` tracks
  // `mutate` directly rather than demanding the whole `mutation` result
  // object as a dep — in TanStack v5, `mutate` (and `reset`) are stable
  // references across renders; the `mutation` object itself is not.
  const { mutate, mutateAsync, reset, status } = mutation;

  const setLayoutMode = useCallback(
    (mode: LayoutMode) => mutate({ layoutMode: mode }),
    [mutate]
  );

  const setPipelineLayoutMode = useCallback(
    (mode: LayoutMode) => mutate({ pipelineLayoutMode: mode }),
    [mutate]
  );

  const setDefaultAccountTab = useCallback(
    (tab: AccountTab) => mutate({ defaultAccountTab: tab }),
    [mutate]
  );

  const setTerraDesignV2 = useCallback(
    (next: boolean) => mutate({ terraDesignV2: next }),
    [mutate]
  );

  const setFindMissingNumbers = useCallback(
    (next: boolean) => mutate({ findMissingNumbers: next }),
    [mutate]
  );

  const setViewAsRole = useCallback(
    (team: FlowRole | null) => mutate({ viewAsRole: team }),
    [mutate]
  );

  /**
   * The one Flow-layout writer, for every slot. `'account'` and `'home'` write
   * their own pref; a table slot merges `id` into the latest intended
   * map: the pending map of writes still in flight, else the LIVE cached map
   * (never a render-time snapshot, so two quick picks on different tables
   * cannot clobber each other, even back to back before the optimistic write), deletes the key for `null`, and stores an
   * empty map as `null`. Resolves once the server holds the value; rejects on a
   * failed write, after `onError` rolled the cache back. Fire-and-forget
   * callers add `.catch(() => {})`: the rollback and the global toast already
   * ran.
   */
  const setFlowLayoutChoice = useCallback(
    async (slot: FlowLayoutSlot, id: FlowListLayoutId | null): Promise<void> => {
      if (slot === 'account') {
        // 'signal-response' is a list-only id; parsing rejects it for the account page.
        await mutateAsync({ flowLayoutAccount: id === null ? null : FlowAccountLayoutIdSchema.parse(id) });
        return;
      }
      if (slot === 'home') {
        await mutateAsync({ flowLayoutHome: id === null ? null : FlowHomeLayoutIdSchema.parse(id) });
        return;
      }
      const table: FlowLayoutTable = slot;
      const pending = pendingFlowLayoutList.get(queryClient);
      const base = pending?.map ?? queryClient.getQueryData<LayoutPrefs>(PREFS_KEY)?.flowLayoutList ?? null;
      const next = { ...base };
      if (id === null) delete next[table];
      else next[table] = id;
      // Synchronously, before any await: the next call merges against this.
      const entry = { map: next, inFlight: (pending?.inFlight ?? 0) + 1 };
      pendingFlowLayoutList.set(queryClient, entry);
      try {
        await mutateAsync({ flowLayoutList: Object.keys(next).length > 0 ? next : null });
      } finally {
        const latest = pendingFlowLayoutList.get(queryClient);
        if (latest) {
          latest.inFlight -= 1;
          // Last write settled: the cache (success, or rolled back on error) is the truth again.
          if (latest.inFlight <= 0) pendingFlowLayoutList.delete(queryClient);
        }
      }
    },
    [queryClient, mutateAsync]
  );

  return {
    layoutMode: current.layoutMode,
    pipelineLayoutMode: current.pipelineLayoutMode,
    defaultAccountTab: current.defaultAccountTab,
    showAllAccounts: current.showAllAccounts,
    terraDesignV2: current.terraDesignV2,
    findMissingNumbers: current.findMissingNumbers,
    viewAsRole: current.viewAsRole,
    flowLayoutAccount: current.flowLayoutAccount,
    flowLayoutHome: current.flowLayoutHome,
    flowLayoutList: current.flowLayoutList,
    /** True once a GET has settled, success or failure; on failure DEFAULTS stand in (terraDesignV2 off). */
    prefsLoaded: query.isFetched,
    /** True once the cache holds the rep's prefs (a successful GET, or an optimistic write seeding it); false while loading and after a failed first GET, when every field above is the DEFAULTS stand-in. useRoleView reads it so a GET failure is never taken as "no lens". */
    prefsAvailable: query.data !== undefined,
    setLayoutMode,
    setPipelineLayoutMode,
    setDefaultAccountTab,
    setTerraDesignV2,
    setFindMissingNumbers,
    setViewAsRole,
    setFlowLayoutChoice,
    /** TanStack's raw mutation status ('idle'|'pending'|'success'|'error') — per hook instance, so a caller's own indicator only reflects its own writes. */
    saveStatus: status,
    resetSaveStatus: reset,
  };
}

/**
 * Per-rep `findMissingNumbers` setting (on by default): `[value, setValue]`. Reads and writes through
 * `useLayoutPrefs`'s one cache entry and mutation (same optimistic semantics as
 * `setTerraDesignV2`), never a second query.
 */
export function useFindMissingNumbersPref(): [boolean, (next: boolean) => void] {
  const { findMissingNumbers, setFindMissingNumbers } = useLayoutPrefs();
  return [findMissingNumbers, setFindMissingNumbers];
}
